/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import * as cookie from './cookie';
import {fetch} from '@adobe/exc-app/network';

/* eslint-disable sort-keys */
const ENVS = {
  test: 'stg1',
  dev: 'stg1',
  qa: 'stg1',
  stage: 'stg1',
  prod: ''
};
/* eslint-enable sort-keys */

/**
 * Get Best Locale Setting using User Preferences and Supported Locales.
 * @param {Object} profile JSON Object for User's IMS Profile.
 * @param {Array.<string>} supportedLanguages List of Supported Locales.
 * @returns {string} Best Suitable Locale.
 */
export const getBestSupportedLocale = (profile, supportedLanguages) => {
  const preferredLanguages = profile.preferred_languages;
  const firstLocale = preferredLanguages && preferredLanguages.length > 0 ? preferredLanguages[0] : 'en-US';
  const secondLocale = preferredLanguages && preferredLanguages.length > 1 ? preferredLanguages[1] : 'en-US';
  const firstLocalePrefix = firstLocale.split('-')[0];
  const secondLocalePrefix = secondLocale.split('-')[0];
  let locale = supportedLanguages.find(lang => lang.toUpperCase() === firstLocale.toUpperCase());
  if (!locale && (firstLocalePrefix !== secondLocalePrefix)) {
    locale = supportedLanguages.find(lang => lang.split('-')[0] === firstLocalePrefix);
  }
  if (!locale) {
    locale = supportedLanguages.find(lang => lang.toUpperCase() === secondLocale.toUpperCase())
      || supportedLanguages.find(lang => lang.split('-')[0] === secondLocalePrefix);
  }
  return locale || 'en-US';
};

function getIMSOrigin(env) {
  return `https://adobeid-na1${/^(prod)$/.test(env) ? '' : '-' + ENVS[env]}.services.adobe.com`;
}

export const getOrgs = async (client_id, env) => {
  const url = `${getIMSOrigin(env)}/ims/organizations/v5?client_id=${client_id}`;
  const res = await fetch(url, {auth: 'Header'});
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return (await res.json()) || [];
};

// Use fetch until we move to IMS lib v2.
export const getToken = async(client_id, scope, env) => {
  const url = `${getIMSOrigin(env)}/ims/check/v1/token?client_id=${client_id}&scope=${scope}`;
  const res = await fetch(url, {credentials: 'include'});
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return (await res.json()) || {};
};

const IMS_ENV_COOKIE = 'shellImsEnv';

/**
 * Validates that the current environment on which we have a session is the same as the
 * requested one. This works around various IMS bugs.
 * @param {string} curImsEnv Currently selected IMS env
 * @param {string} buildEnv Current build environment (dev|qa|stage|prod)
 * @returns {boolean}
 */
export function isSessionEnvValid(curImsEnv, buildEnv) {
  const prevImsEnv = cookie.get(IMS_ENV_COOKIE);
  const standardImsEnv = buildEnv === 'prod' ? 'prod' : 'stage';
  let conflictingEnv;

  // The default IMS env is being used but the previous IMS environment is
  // different, we should sign out of the previous environment.
  if (standardImsEnv === curImsEnv && prevImsEnv && curImsEnv !== prevImsEnv) {
    conflictingEnv = prevImsEnv;
  }
  // The default IMS env is NOT being used (e.g. `shell_ims`)
  if (standardImsEnv !== curImsEnv) {
    // No previous IMS env, this means that the user hasn't gone through the
    // updated code yet. Sign out of the standard env just to be safe.
    if (!prevImsEnv) {
      conflictingEnv = standardImsEnv;

    // There is a previous IMS env and it's not the same as the current one.
    // Sign out of the previous IMS env so we can use the current one.
    } else if (prevImsEnv && curImsEnv !== prevImsEnv) {
      conflictingEnv = prevImsEnv;
    }
  }

  // The auth env wasn't updated, so we can continue on.
  if (!conflictingEnv) {
    cookie.set(IMS_ENV_COOKIE, curImsEnv, true);
    return true;
  }

  cookie.del(IMS_ENV_COOKIE, true);
  return false;
}
